/**
 * Lightweight coordination layer for the monthly Application Feedback popup.
 *
 * Other parts of the app (notably the dashboard banner auto-open logic) need
 * to know whether the feedback survey is currently showing — or is about to
 * show — so they don't stack two modals on top of each other.
 *
 * We intentionally avoid Redux here to keep the popup self-contained.
 */

const OPENED_EVENT = 'feedback-survey:opened'
const CLOSED_EVENT = 'feedback-survey:closed'

type GlobalWithFlag = typeof window & {__feedbackSurveyOpen?: boolean}

const getGlobal = (): GlobalWithFlag | null => {
  if (typeof window === 'undefined') return null
  return window as GlobalWithFlag
}

export const isFeedbackSurveyOpen = (): boolean => {
  return getGlobal()?.__feedbackSurveyOpen === true
}

export const markFeedbackSurveyOpened = (): void => {
  const g = getGlobal()
  if (!g) return
  g.__feedbackSurveyOpen = true
  g.dispatchEvent(new Event(OPENED_EVENT))
}

export const markFeedbackSurveyClosed = (): void => {
  const g = getGlobal()
  if (!g) return
  g.__feedbackSurveyOpen = false
  g.dispatchEvent(new Event(CLOSED_EVENT))
}

export const onFeedbackSurveyOpened = (handler: () => void): (() => void) => {
  const g = getGlobal()
  if (!g) return () => {}
  g.addEventListener(OPENED_EVENT, handler)
  return () => g.removeEventListener(OPENED_EVENT, handler)
}

export const onFeedbackSurveyClosed = (handler: () => void): (() => void) => {
  const g = getGlobal()
  if (!g) return () => {}
  g.addEventListener(CLOSED_EVENT, handler)
  return () => g.removeEventListener(CLOSED_EVENT, handler)
}
